import type { CmmdTaskStatus } from "@cmmd-forge/contracts";

export interface StartThreadFlowResult {
  readonly autoStarted: boolean;
  readonly providerHint: string | null;
  readonly threadId?: string;
  readonly environmentId?: string;
}

export interface StartThreadFlowDependencies {
  readonly startThreadForItemInSidebar: (itemId: string) => Promise<StartThreadFlowResult>;
  readonly updateTaskStatus: (
    itemId: string,
    status: CmmdTaskStatus,
    options?: { promptStartThread?: boolean },
  ) => void | Promise<void>;
  readonly toast: (input: {
    type: "success" | "warning" | "error";
    title: string;
    description: string;
  }) => void;
  readonly onThreadCreated?: (result: StartThreadFlowResult) => void | Promise<void>;
}

/**
 * Start-thread action contract (list/detail surface):
 * - Create/start the thread first.
 * - Mark task in_progress after thread creation succeeds, before the new thread view renders.
 * - If thread start fails, do not mutate task status.
 */
export async function runTransactionalStartThread(
  itemId: string,
  deps: StartThreadFlowDependencies,
): Promise<void> {
  try {
    const result = await deps.startThreadForItemInSidebar(itemId);

    const taskStatusUpdate = (async (): Promise<unknown | null> => {
      // CMMD workflow tasks require an exact workflowStageId. This generic
      // Command Center action has only a category, so CMMD remains authoritative
      // and the task status is left unchanged instead of guessing a stage.
      if (itemId.startsWith("cmmd:")) return null;
      try {
        await deps.updateTaskStatus(itemId, "in_progress", { promptStartThread: false });
        return null;
      } catch (error) {
        return error;
      }
    })();
    await deps.onThreadCreated?.(result);

    const taskStatusError = await taskStatusUpdate;
    if (taskStatusError) {
      deps.toast({
        type: "warning",
        title: "Thread started, task sync pending",
        description:
          "Forge created the thread, but CMMD did not confirm the task status update. You can keep working and retry sync from Projects.",
      });
      return;
    }

    if (result.autoStarted) {
      deps.toast({
        type: "success",
        title: "Thread started",
        description: "A new thread has been created in the sidebar for this task.",
      });
      return;
    }

    deps.toast({
      type: "warning",
      title: "Thread created, auto-start skipped",
      description:
        result.providerHint ??
        "The selected provider is currently unavailable. Open the thread and choose an available provider to continue.",
    });
  } catch (error) {
    deps.toast({
      type: "error",
      title: "Could not start thread",
      description: error instanceof Error ? error.message : "An unexpected error occurred.",
    });
  }
}
